import React from "react";
import { API } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronDown, Users as UsersIcon, CalendarRange, X, Bot } from "lucide-react";

// Shared due-date picker (shadcn Popover + Calendar) used across Kanban quick-add,
// the Scheduler and the task detail dialog for one consistent calendar everywhere.
export function DuePicker({ value, onChange, disabled, compact = false, testid = "due", placeholder = "No due date", allowClear = true }) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const label = selected ? selected.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : placeholder;
  const trigCls = compact
    ? "w-full inline-flex items-center gap-1.5 rounded-lg border border-[#EEEFEF] bg-[#F6F6FD] px-2.5 py-1.5 pr-7 text-xs text-left disabled:opacity-60 hover:border-[#d9ccff] transition-colors"
    : "w-full inline-flex items-center gap-2 rounded-lg border border-[#EEEFEF] bg-[#F6F6FD] px-3 h-9 pr-8 text-sm text-left disabled:opacity-60 hover:border-[#d9ccff] transition-colors";
  const iconCls = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <div className="relative">
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid} disabled={disabled} className={trigCls}>
            <CalendarRange className={`${iconCls} text-[#9FA1A4] shrink-0`} />
            <span className={selected ? "text-[#06060C]" : "text-[#9FA1A4]"}>{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0 bg-white border-[#EEEFEF]" data-testid={`${testid}-calendar`}>
          <Calendar mode="single" selected={selected} onSelect={(d) => { if (!d) return; const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setOpen(false); onChange(s); }} />
        </PopoverContent>
      </Popover>
      {allowClear && selected && !disabled && <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#c4c5cc] hover:text-rose-600"><X className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} /></button>}
    </div>
  );
}

const TIME_OPTIONS = (() => { const a = []; for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) a.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`); return a; })();

// 24-hour time picker (15-min steps). Value is always "HH:MM" 24h.
export function TimeSelect({ value, onChange, testid = "time", placeholder = "--:--", disabled = false }) {
  return (
    <div className="relative">
      <select data-testid={testid} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-lg border border-[#EEEFEF] bg-[#F6F6FD] px-2.5 h-9 pr-7 text-sm text-[#06060C] disabled:opacity-60 hover:border-[#d9ccff] transition-colors font-mono-data">
        <option value="">{placeholder}</option>
        {value && !TIME_OPTIONS.includes(value) && <option value={value}>{value}</option>}
        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <ChevronDown className="h-3.5 w-3.5 text-[#9FA1A4] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}


export function UserAvatar({ name, uid, hasAvatar, size = 24, isAgent = false, color = "#723FFF" }) {
  const initial = ((name || "?").trim().charAt(0) || "?").toUpperCase();
  const agent = isAgent || (typeof uid === "string" && uid.startsWith("agent:"));
  if (agent) {
    const aid = typeof uid === "string" && uid.startsWith("agent:") ? uid.slice(6) : uid;
    return (
      <span className="relative shrink-0 inline-flex" style={{ width: size, height: size }} title={`${name || "AI agent"} · AI teammate`}>
        {hasAvatar && aid
          ? <img src={`${API}/topo/agents/${aid}/avatar`} alt={name} className="rounded-lg object-cover" style={{ width: size, height: size }} />
          : <span className="rounded-lg flex items-center justify-center text-white font-semibold" style={{ width: size, height: size, backgroundColor: color }}><Bot style={{ width: size * 0.6, height: size * 0.6 }} /></span>}
        <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white flex items-center justify-center" style={{ width: size * 0.44, height: size * 0.44 }}>
          <Bot style={{ width: size * 0.34, height: size * 0.34, color }} />
        </span>
      </span>
    );
  }
  if (hasAvatar && uid) return <img src={`${API}/users/${uid}/avatar`} alt={name} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  return <span className="rounded-full flex items-center justify-center shrink-0 bg-[#EDE7FF] text-[#5b2fd6] font-semibold" style={{ width: size, height: size, fontSize: size * 0.42 }}>{initial}</span>;
}

export function fmtMinutes(m) {
  m = m || 0;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// Single colored tag pill, used on task rows, kanban cards, detail dialog and client views.
export function TagChip({ tag }) {
  if (!tag) return null;
  const c = tag.color || "#723FFF";
  return (
    <span data-testid={`tag-chip-${tag.id}`} className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap" style={{ color: c, borderColor: `${c}55`, backgroundColor: `${c}14` }}>
      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: c }} />{tag.name}
    </span>
  );
}

// Single-select tag picker (choose one tag or clear).
export function TagPicker({ tags = [], value, onChange, disabled, testid = "tag" }) {
  const current = tags.find((t) => t.id === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`${testid}-picker`} disabled={disabled} className="w-full inline-flex items-center gap-2 rounded-lg border border-[#EEEFEF] bg-[#F6F6FD] px-3 h-9 text-sm text-left disabled:opacity-60 hover:border-[#d9ccff] transition-colors">
          {current ? <TagChip tag={current} /> : <span className="text-[#9FA1A4]">No tag</span>}
          <ChevronDown className="h-3.5 w-3.5 text-[#9FA1A4] ml-auto shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-white border-[#EEEFEF] max-h-72 overflow-y-auto w-52">
        <DropdownMenuItem data-testid={`${testid}-pick-none`} onClick={() => onChange(null)} className="cursor-pointer focus:bg-[#F6F6FD] text-[#595D62]">No tag</DropdownMenuItem>
        {tags.map((t) => <DropdownMenuItem key={t.id} data-testid={`${testid}-pick-${t.id}`} onClick={() => onChange(t.id)} className="cursor-pointer focus:bg-[#F6F6FD]"><TagChip tag={t} /></DropdownMenuItem>)}
        {!tags.length && <div className="px-2 py-1.5 text-xs text-[#9FA1A4]">No tags created yet</div>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Filters a task list by "My tasks" (mode=mine) or by a set of assignee ids.
export function applyTaskFilter(tasks, { mode, meId, assigneeIds }) {
  return (tasks || []).filter((t) => {
    if (mode === "mine") return t.assignee && t.assignee.id === meId;
    if (assigneeIds && assigneeIds.length) return t.assignee && assigneeIds.includes(t.assignee.id);
    return true;
  });
}

// "My tasks / All tasks" segmented control + a multi-select people filter (shown for All tasks).
export function TaskAssigneeFilter({ mode, setMode, users = [], assigneeIds = [], setAssigneeIds, testid = "kanban" }) {
  const toggle = (id) => setAssigneeIds(assigneeIds.includes(id) ? assigneeIds.filter((x) => x !== id) : [...assigneeIds, id]);
  const label = assigneeIds.length === 0 ? "All people" : `${assigneeIds.length} selected`;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-task-filter`}>
      <div className="inline-flex rounded-full border border-[#EEEFEF] bg-white p-0.5">
        <button data-testid={`${testid}-filter-mine`} onClick={() => setMode("mine")} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${mode === "mine" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:text-[#06060C]"}`}>My tasks</button>
        <button data-testid={`${testid}-filter-all`} onClick={() => setMode("all")} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${mode === "all" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:text-[#06060C]"}`}>All tasks</button>
      </div>
      {mode === "all" && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`${testid}-filter-people`} className={`inline-flex items-center gap-1.5 h-8 rounded-full border border-[#EEEFEF] bg-white px-3 text-xs font-medium ${assigneeIds.length ? "text-[#06060C]" : "text-[#595D62]"} hover:bg-[#F6F6FD]`}>
              <UsersIcon className="h-3.5 w-3.5" /> {label} <ChevronDown className="h-3.5 w-3.5 text-[#9FA1A4]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-white border-[#EEEFEF] max-h-72 overflow-y-auto w-56">
            {assigneeIds.length > 0 && <><DropdownMenuItem data-testid={`${testid}-filter-people-clear`} onClick={() => setAssigneeIds([])} className="cursor-pointer text-[#5b2fd6] focus:bg-[#F6F6FD]">Clear ({assigneeIds.length})</DropdownMenuItem><DropdownMenuSeparator className="bg-[#f0f0f3]" /></>}
            {users.map((u) => (
              <DropdownMenuCheckboxItem key={u.id} data-testid={`${testid}-filter-person-${u.id}`} checked={assigneeIds.includes(u.id)} onCheckedChange={() => toggle(u.id)} onSelect={(e) => e.preventDefault()} className="cursor-pointer focus:bg-[#F6F6FD]">{u.name}</DropdownMenuCheckboxItem>
            ))}
            {!users.length && <div className="px-2 py-1.5 text-xs text-[#9FA1A4]">No teammates</div>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

